import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { numberFormatterInternational } from 'app/utils/telephonyHelpers';
import { flowNodeColor } from 'fable/flowEditor';
import { PhoneExtraData } from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import { Text } from 'flow-builder/node/text';
import { TextContent } from 'flow-builder/node/textContent';
import { TextEllipsis } from 'flow-builder/node/textEllipsis';
import { hexStringToNumber } from 'flow-builder/renderers/common/colors';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'flow-builder/types/node';

import { Callback as CallbackModal } from '../../modals/callback';
import type { AnnouncementPayloadT } from '../../types';

export type Payload = {
  announcement: null | AnnouncementPayloadT;
  digit: string;
  from_number: null | string;
  next_node_id: null | string;
};

type CallbackNodeAttributes = GenericNodeAttributes<Payload, PhoneExtraData>;
type CallbackNodeProps = GenericNodeProps<Payload, PhoneExtraData>;

const translatePayload: CallbackNodeAttributes['translatePayload'] = (node) => {
  const { next_node_id } = node.payload;

  return [
    {
      id: uuid(),
      connection: next_node_id || uuid(),
      label: 'arrow',
    },
  ];
};

const updateConnection: CallbackNodeAttributes['updateConnection'] = (
  node,
  _connection,
  connectionId,
) => {
  const payload = { ...node.payload };

  payload.next_node_id = connectionId;

  return { ...node, payload };
};

export const CallbackNodeAttributes: CallbackNodeAttributes = {
  iconName: 'callback',
  label: 'Callback',
  color: hexStringToNumber(flowNodeColor.orange),
  translatePayload,
  updateConnection,
  NodeContent: CallbackNode,
  ActionModal: CallbackModal,
};

export const useAnnouncementName = (
  announcement: AnnouncementPayloadT | null,
  extraData: PhoneExtraData,
): string => {
  const { announcements: extraDataAnnouncements } = extraData;

  return useMemo(() => {
    const { url, label } = announcement?.file || {};

    if (label) {
      return label;
    }

    if (url) {
      const announcementInExtraData = extraDataAnnouncements.find(
        (announcement) => announcement.url === url,
      );

      if (announcementInExtraData?.pretty_name) {
        return announcementInExtraData.pretty_name;
      }

      const matchedUrlLabel = /([^/]+$)/.exec(url)?.toString();

      if (matchedUrlLabel) {
        return matchedUrlLabel;
      }
    }

    return 'None';
  }, [announcement, extraDataAnnouncements]);
};

export function CallbackNode({
  node,
  context,
}: CallbackNodeProps): JSX.Element {
  const { extraData } = context;
  const { payload } = node;

  const announcement = useAnnouncementName(payload.announcement, extraData);
  const usedNumber = payload?.from_number || extraData.number;
  const phoneNumber = usedNumber
    ? numberFormatterInternational({ phoneNumber: usedNumber })
    : 'No number selected';

  return (
    <TextContent>
      {useMemo(
        () => (
          <>
            <TextEllipsis>
              <Text weight="normal" text="Announcement" />
              <Text weight="semibold" text={announcement} />
            </TextEllipsis>
            <TextEllipsis offset={{ y: 5, x: 0 }}>
              <Text weight="normal" text="Outbound number" />
              <Text weight="semibold" text={phoneNumber} />
            </TextEllipsis>
          </>
        ),
        [announcement, phoneNumber],
      )}
    </TextContent>
  );
}
